import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { api, money } from "@/lib/api";
import DocumentDialog from "@/components/DocumentDialog";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, DotsThreeVertical, PaperPlaneTilt, CheckCircle, PencilSimple, Copy, FileArrowDown, Money, HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Invoices() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState(searchParams.get("status") === "paid" ? "paid" : searchParams.get("status") === "sent" ? "unpaid" : "all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payInv, setPayInv] = useState(null);
  const [payMethod, setPayMethod] = useState("cash");
  const [payRef, setPayRef] = useState("");
  const [paying, setPaying] = useState(false);

  const load = () => api.listInvoices().then(setInvoices).catch(() => {});
  useEffect(() => { load(); }, []);

  const shown = invoices.filter((i) => filter === "all" ? true : filter === "paid" ? i.status === "paid" : i.status !== "paid");

  const send = async (inv) => {
    try {
      const res = await api.sendInvoice(inv.id);
      if (res.email?.status === "success") toast.success(`Invoice emailed to ${inv.client_email}`);
      else if (res.email?.status === "skipped") toast.warning("Invoice marked sent (email not configured yet)");
      else toast.warning("Invoice marked sent (email failed)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
  };
  const markPaid = async (inv) => { await api.markPaid(inv.id); toast.success("Marked paid"); load(); };
  const copyLink = (inv) => { navigator.clipboard.writeText(`${window.location.origin}/pay/${inv.id}`); toast.success("Payment link copied"); };
  const openCollect = (inv) => { setPayInv(inv); setPayMethod("cash"); setPayRef(""); };
  const collect = async () => {
    setPaying(true);
    try {
      await api.markPaid(payInv.id, { method: payMethod, reference: payRef });
      toast.success(`Payment recorded for ${payInv.number}`);
      setPayInv(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not record payment"); }
    finally { setPaying(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <PageHeader title="Invoices" subtitle="Billing & online payments" actions={
        <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="invoice-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All invoices</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#0B1726] hover:bg-slate-800" data-testid="add-invoice-button"><Plus size={18} className="mr-1" /> New Invoice</Button>
        </div>
      } />

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Invoice #</TableHead><TableHead>Client</TableHead><TableHead>Due</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="w-10"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No invoices{filter !== "all" ? " for this filter" : ""} yet.</TableCell></TableRow>}
            {shown.map((inv) => (
              <TableRow key={inv.id} className="hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-mono font-semibold text-[#0B1726]">{inv.number}</TableCell>
                <TableCell>{inv.client_name}</TableCell>
                <TableCell className="text-sm text-slate-500">{inv.due_date || "—"}</TableCell>
                <TableCell><StatusBadge status={inv.status} /></TableCell>
                <TableCell className="text-right font-semibold">{money(inv.total)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`invoice-menu-${inv.id}`}><DotsThreeVertical size={20} weight="bold" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setEditing(inv); setOpen(true); }} disabled={inv.status === "paid"}><PencilSimple size={16} className="mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => send(inv)} data-testid={`invoice-send-${inv.id}`}><PaperPlaneTilt size={16} className="mr-2" /> Send to client</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => window.open(api.invoicePdfUrl(inv.id), "_blank")} data-testid={`invoice-pdf-${inv.id}`}><FileArrowDown size={16} className="mr-2" /> Download PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copyLink(inv)}><Copy size={16} className="mr-2" /> Copy payment link</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openCollect(inv)} disabled={inv.status === "paid"} data-testid={`invoice-collect-${inv.id}`}><HandCoins size={16} className="mr-2 text-emerald-600" /> Collect payment (in person)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => markPaid(inv)} disabled={inv.status === "paid"}><CheckCircle size={16} className="mr-2" /> Mark paid</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <DocumentDialog open={open} onClose={() => setOpen(false)} type="invoice" initial={editing} onSaved={load} />

      <Dialog open={!!payInv} onOpenChange={(v) => !v && setPayInv(null)}>
        <DialogContent data-testid="collect-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><HandCoins size={20} weight="duotone" className="text-emerald-600" /> Collect Payment</DialogTitle>
            <DialogDescription>{payInv && <>Record an in-person payment of <b className="text-slate-800">{money(payInv.amount_due != null && payInv.amount_due > 0 ? payInv.amount_due : payInv.total)}</b> for invoice {payInv.number} — {payInv.client_name}.</>}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Payment method</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {[["cash", "Cash"], ["check", "Check"], ["card", "Card"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setPayMethod(v)} data-testid={`pay-method-${v}`}
                    className={`rounded-lg border p-2.5 text-sm font-semibold transition-colors duration-200 ${payMethod === v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
                ))}
              </div>
            </div>
            {payMethod === "card" && payInv && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <Label className="text-slate-700">Online card payment</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">Open the secure payment page to charge the client's card now.</p>
                <a href={`${window.location.origin}/pay/${payInv.id}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1726] underline underline-offset-2 hover:text-emerald-700 transition-colors duration-200"
                  data-testid="pay-online-link">
                  <Money size={16} weight="duotone" /> Open card payment page
                </a>
              </div>
            )}
            <div>
              <Label>Reference <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder={payMethod === "check" ? "Check #" : "Note / confirmation #"} className="mt-1.5" data-testid="pay-reference" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayInv(null)}>Cancel</Button>
            <Button onClick={collect} disabled={paying} className="bg-emerald-600 hover:bg-emerald-700" data-testid="collect-payment-confirm">{paying ? "Recording…" : "Record Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
